import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { HandCoins, Loader2, Plus, Pencil, Trash2, Users, Calculator, FileDown, FileText, Landmark, RefreshCw, CheckCircle2, ChevronDown, ChevronRight, Mail, Send, FileUp, History, Save, RotateCcw, FileSpreadsheet, BookmarkPlus, X, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { formatDeNumber } from '@/lib/utils';
import { useActionBarConfig } from '../../contexts/ActionBarContext';
import { formatTime } from '@/lib/utils';
import PlusHoursAdminTab from './PlusHoursAdminTab';
import BankImportDialog from './BankImportDialog';
import DocumentEmailDialog from '@/components/email/DocumentEmailDialog';
import FintsSubmitDialog from '../Billing/FintsSubmitDialog';
import SteuerberaterUpload from './SteuerberaterUpload';
import BulkEmailRecipientsDialog from '@/components/email/BulkEmailRecipientsDialog';

const PAYROLL_TABS = new Set(['profiles', 'runs', 'plus-hours']);

const fmtEur = (v) => `${formatDeNumber(v)} €`;
const EMPTY_WAGE_CONFIG = {
  lohn1_max_participants: 4,
  lohn2_min_participants: 5,
  prepaid_count_advance: false,
  holiday_factor: 4.33,
  vacation_share: 0.15,
};

const EMPTY = {
  teacher_id: '', display_name: '', employment_type: 'honorar', personnel_number: '',
  monthly_salary: 0, hourly_rate: 0, per_enrollment_rate: 0, revenue_share_percent: 0,
  iban: '', bic: '', account_holder: '', notes: '', is_active: true, payout_blocked: false, overrides: [],
  surcharges: [], weekly_activities: [],
  wage_config: { ...EMPTY_WAGE_CONFIG },
};

// Exportiert, damit der Dialog einzeln getestet werden kann: die ganze Seite zu
// rendern dauert unter Last ein Vielfaches und macht den Testlauf unruhig.
export function ProfileDialog({ open, onClose, profile, teachers, subjects, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!profile?.id;

  useEffect(() => {
    if (open) {
      setForm(profile
        ? {
            ...EMPTY,
            ...profile,
            teacher_id: profile.teacher_id || '',
            surcharges: (profile.surcharges || []).map((s) => ({ ...s })),
            weekly_activities: (profile.weekly_activities || []).map((a) => ({ ...a })),
            wage_config: { ...EMPTY_WAGE_CONFIG, ...(profile.wage_config || {}) },
          }
        : { ...EMPTY, wage_config: { ...EMPTY_WAGE_CONFIG }, surcharges: [], weekly_activities: [] });
    }
  }, [open, profile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === '' ? 0 : parseFloat(String(v).replace(',', '.')) || 0);

  // Nur Platzhalter: zeigt, welcher Name ohne eigenen Eintrag übertragen wird.
  const gewaehlteLehrkraft = (teachers || []).find((t) => t.id === form.teacher_id);
  const nameFuerKonto = (
    (gewaehlteLehrkraft && `${gewaehlteLehrkraft.first_name || ''} ${gewaehlteLehrkraft.last_name || ''}`)
    || form.display_name
    || profile?.name
    || ''
  ).trim();

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_salary: num(form.monthly_salary), hourly_rate: num(form.hourly_rate),
        per_enrollment_rate: num(form.per_enrollment_rate), revenue_share_percent: num(form.revenue_share_percent),
        overrides: (form.overrides || []).map((o) => ({
          subject_id: o.subject_id,
          hourly_rate: o.hourly_rate === '' || o.hourly_rate == null ? null : num(o.hourly_rate),
          per_enrollment_rate: o.per_enrollment_rate === '' || o.per_enrollment_rate == null ? null : num(o.per_enrollment_rate),
        })),
        surcharges: (form.surcharges || [])
          .filter((s) => (s.label || '').trim() && num(s.amount) !== 0 && s.valid_from)
          .map((s) => ({
            label: (s.label || '').trim(),
            amount: num(s.amount),
            valid_from: s.valid_from,
            valid_until: s.valid_until || null,
          })),
        weekly_activities: (form.weekly_activities || [])
          .filter((a) => (a.label || '').trim() && num(a.hours_per_week) > 0 && a.valid_from)
          .map((a) => ({
            label: (a.label || '').trim(),
            hours_per_week: num(a.hours_per_week),
            wage_band: a.wage_band === 'lohn2' ? 'lohn2' : 'lohn1',
            valid_from: a.valid_from,
            valid_until: a.valid_until || null,
          })),
      };
      if (isEdit) await api.put(`/payroll/profiles/${profile.id}`, payload);
      else await api.post('/payroll/profiles', payload);
      toast.success('Profil gespeichert – Entwurfs-Läufe automatisch neu berechnet');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const addOverride = () => set('overrides', [...(form.overrides || []), { subject_id: '', hourly_rate: '', per_enrollment_rate: '' }]);
  const setOverride = (idx, k, v) => set('overrides', form.overrides.map((o, i) => (i === idx ? { ...o, [k]: v } : o)));
  const removeOverride = (idx) => set('overrides', form.overrides.filter((_, i) => i !== idx));

  const addSurcharge = () => set('surcharges', [...(form.surcharges || []), {
    label: '', amount: '', valid_from: new Date().toISOString().slice(0, 10), valid_until: '',
  }]);
  const setSurcharge = (idx, k, v) => set('surcharges', form.surcharges.map((s, i) => (i === idx ? { ...s, [k]: v } : s)));
  const removeSurcharge = (idx) => set('surcharges', form.surcharges.filter((_, i) => i !== idx));

  const addWeekly = () => set('weekly_activities', [...(form.weekly_activities || []), {
    label: '', hours_per_week: '', wage_band: 'lohn1',
    valid_from: new Date().toISOString().slice(0, 10), valid_until: '',
  }]);
  const setWeekly = (idx, k, v) => set('weekly_activities', form.weekly_activities.map((a, i) => (i === idx ? { ...a, [k]: v } : a)));
  const removeWeekly = (idx) => set('weekly_activities', form.weekly_activities.filter((_, i) => i !== idx));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payroll-profile-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Vergütungsprofil bearbeiten' : 'Mitarbeiter / Profil anlegen'}</DialogTitle>
          <DialogDescription>Alle Komponenten wirken additiv – Mischmodelle (z. B. Festgehalt + Umsatzbeteiligung) sind möglich.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Lehrkraft (optional)</label>
              <Select value={form.teacher_id || 'none'} onValueChange={(v) => set('teacher_id', v === 'none' ? '' : v)} disabled={isEdit}>
                <SelectTrigger className="mt-1" data-testid="payroll-form-teacher"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">– Verwaltungsmitarbeiter (ohne Lehrer) –</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Beschäftigungsart</label>
              <Select value={form.employment_type} onValueChange={(v) => set('employment_type', v)}>
                <SelectTrigger className="mt-1" data-testid="payroll-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="honorar">Honorarkraft</SelectItem>
                  <SelectItem value="angestellt">Festangestellt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.employment_type === 'angestellt' && (
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-500">Personalnummer (Lohnbüro / Wolters Kluwer)</label>
                <Input value={form.personnel_number || ''} onChange={(e) => set('personnel_number', e.target.value)} placeholder="z. B. 00123" className="mt-1 font-mono" data-testid="payroll-form-personnel-number" />
              </div>
            )}
          </div>
          {!form.teacher_id && (
            <div>
              <label className="text-xs font-medium text-slate-500">Name *</label>
              <Input value={form.display_name || ''} onChange={(e) => set('display_name', e.target.value)} placeholder="z. B. Maria Muster (Verwaltung)" className="mt-1" data-testid="payroll-form-name" />
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Vergütungskomponenten (additiv)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Festgehalt €/Monat</label>
                <Input value={form.monthly_salary} onChange={(e) => set('monthly_salary', e.target.value)} className="mt-1" data-testid="payroll-form-salary" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Stundensatz €/Unterrichtsstunde</label>
                <Input value={form.hourly_rate} onChange={(e) => set('hourly_rate', e.target.value)} className="mt-1" data-testid="payroll-form-hourly" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Pauschale €/Belegung/Monat</label>
                <Input value={form.per_enrollment_rate} onChange={(e) => set('per_enrollment_rate', e.target.value)} className="mt-1" data-testid="payroll-form-perenrollment" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Umsatzbeteiligung % (Tarif-Monatsumsatz)</label>
                <Input value={form.revenue_share_percent} onChange={(e) => set('revenue_share_percent', e.target.value)} className="mt-1" data-testid="payroll-form-share" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="payroll-surcharges">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Regelmäßige Zuschläge</div>
                <p className="text-[11px] text-slate-500 mt-0.5">Fester Betrag €/Monat mit Gültigkeit (z.&nbsp;B. Funktionszulage).</p>
              </div>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addSurcharge} data-testid="payroll-add-surcharge">
                <Plus className="h-3.5 w-3.5 mr-1" /> Zuschlag
              </Button>
            </div>
            {(form.surcharges || []).length === 0 && <p className="text-xs text-slate-400">Keine Zuschläge.</p>}
            {(form.surcharges || []).map((s, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_5.5rem_7rem_7rem_auto] gap-2 items-end">
                <div>
                  <label className="text-[10px] text-slate-400">Bezeichnung</label>
                  <Input value={s.label || ''} onChange={(e) => setSurcharge(idx, 'label', e.target.value)}
                    placeholder="z. B. Fachbereichsleitung" className="h-8 text-xs" data-testid={`payroll-surcharge-label-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">€/Monat</label>
                  <Input value={s.amount ?? ''} onChange={(e) => setSurcharge(idx, 'amount', e.target.value)}
                    className="h-8 text-xs" data-testid={`payroll-surcharge-amount-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">Gültig ab</label>
                  <Input type="date" value={s.valid_from || ''} onChange={(e) => setSurcharge(idx, 'valid_from', e.target.value)}
                    className="h-8 text-xs" data-testid={`payroll-surcharge-from-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">bis (optional)</label>
                  <Input type="date" value={s.valid_until || ''} onChange={(e) => setSurcharge(idx, 'valid_until', e.target.value)}
                    className="h-8 text-xs" data-testid={`payroll-surcharge-until-${idx}`} />
                </div>
                <button type="button" onClick={() => removeSurcharge(idx)} className="p-1.5 mb-0.5 text-slate-400 hover:text-rose-600" aria-label="Zuschlag entfernen">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="payroll-weekly-activities">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Wöchentliche Zusatzaktivitäten</div>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Std./Woche × Ferienfaktor (wie Unterricht) → Lohnmeldung; Vergütung mit Profil-Stundensatz.
                </p>
              </div>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addWeekly} data-testid="payroll-add-weekly">
                <Plus className="h-3.5 w-3.5 mr-1" /> Aktivität
              </Button>
            </div>
            {(form.weekly_activities || []).length === 0 && <p className="text-xs text-slate-400">Keine Zusatzaktivitäten.</p>}
            {(form.weekly_activities || []).map((a, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_4.5rem_5.5rem_7rem_7rem_auto] gap-2 items-end">
                <div>
                  <label className="text-[10px] text-slate-400">Bezeichnung</label>
                  <Input value={a.label || ''} onChange={(e) => setWeekly(idx, 'label', e.target.value)}
                    placeholder="z. B. Ensembleleitung" className="h-8 text-xs" data-testid={`payroll-weekly-label-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">Std./Wo.</label>
                  <Input value={a.hours_per_week ?? ''} onChange={(e) => setWeekly(idx, 'hours_per_week', e.target.value)}
                    className="h-8 text-xs" data-testid={`payroll-weekly-hours-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">Lohnband</label>
                  <Select value={a.wage_band || 'lohn1'} onValueChange={(v) => setWeekly(idx, 'wage_band', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`payroll-weekly-band-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lohn1">Lohn 1</SelectItem>
                      <SelectItem value="lohn2">Lohn 2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">Gültig ab</label>
                  <Input type="date" value={a.valid_from || ''} onChange={(e) => setWeekly(idx, 'valid_from', e.target.value)}
                    className="h-8 text-xs" data-testid={`payroll-weekly-from-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">bis (optional)</label>
                  <Input type="date" value={a.valid_until || ''} onChange={(e) => setWeekly(idx, 'valid_until', e.target.value)}
                    className="h-8 text-xs" data-testid={`payroll-weekly-until-${idx}`} />
                </div>
                <button type="button" onClick={() => removeWeekly(idx)} className="p-1.5 mb-0.5 text-slate-400 hover:text-rose-600" aria-label="Aktivität entfernen">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {form.teacher_id && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Abweichende Sätze je Fach</div>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addOverride} data-testid="payroll-add-override">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Fach-Satz
                </Button>
              </div>
              {(form.overrides || []).length === 0 && <p className="text-xs text-slate-400">Keine – es gelten die Standard-Sätze oben.</p>}
              {(form.overrides || []).map((o, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Select value={o.subject_id || ''} onValueChange={(v) => setOverride(idx, 'subject_id', v)}>
                    <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`payroll-override-subject-${idx}`}><SelectValue placeholder="Fach wählen…" /></SelectTrigger>
                    <SelectContent>
                      {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={o.hourly_rate ?? ''} onChange={(e) => setOverride(idx, 'hourly_rate', e.target.value)} placeholder="€/Std." className="w-24 h-8 text-xs" />
                  <Input value={o.per_enrollment_rate ?? ''} onChange={(e) => setOverride(idx, 'per_enrollment_rate', e.target.value)} placeholder="€/Beleg." className="w-24 h-8 text-xs" />
                  <button onClick={() => removeOverride(idx)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}

          {form.teacher_id && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="payroll-wage-config">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Lohnmeldung (Lohn 1 / Lohn 2)</div>
              <p className="text-[11px] text-slate-500">
                Individuelle Schwellen: z.&nbsp;B. Gruppen bis 4 → Lohn 1, ab 5 → Lohn 2. Von den Wochenstunden geht der
                Ferienüberhang ab (Standard 6/40 = 15&nbsp;%), der Rest wird mit dem Ferienfaktor
                (Standard 52/12 ≈ 4,33) in Monatsstunden umgerechnet.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Lohn 1: max. Teilnehmer</label>
                  <Input
                    type="number" min={1}
                    value={form.wage_config?.lohn1_max_participants ?? 4}
                    onChange={(e) => set('wage_config', {
                      ...form.wage_config,
                      lohn1_max_participants: Math.max(1, parseInt(e.target.value || '1', 10)),
                    })}
                    className="mt-1" data-testid="payroll-form-lohn1-max"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Lohn 2: ab Teilnehmer</label>
                  <Input
                    type="number" min={1}
                    value={form.wage_config?.lohn2_min_participants ?? 5}
                    onChange={(e) => set('wage_config', {
                      ...form.wage_config,
                      lohn2_min_participants: Math.max(1, parseInt(e.target.value || '1', 10)),
                    })}
                    className="mt-1" data-testid="payroll-form-lohn2-min"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Ferienüberhang (Anteil)</label>
                  <Input
                    type="number" step="0.01" min={0} max={0.99}
                    value={form.wage_config?.vacation_share ?? 0.15}
                    onChange={(e) => set('wage_config', {
                      ...form.wage_config,
                      vacation_share: parseFloat(e.target.value || '0') || 0,
                    })}
                    className="mt-1" data-testid="payroll-form-vacation-share"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">0,15 = 6 von 40 Wochen</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Ferienfaktor (Monatsstunden)</label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={form.wage_config?.holiday_factor ?? 4.33}
                    onChange={(e) => set('wage_config', {
                      ...form.wage_config,
                      holiday_factor: parseFloat(e.target.value || '0') || 0,
                    })}
                    className="mt-1" data-testid="payroll-form-holiday-factor"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-start gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!form.wage_config?.prepaid_count_advance}
                      onChange={(e) => set('wage_config', {
                        ...form.wage_config,
                        prepaid_count_advance: e.target.checked,
                      })}
                      data-testid="payroll-form-prepaid-advance"
                    />
                    <span>Karten-/Kontingent: noch nicht erteilte, aber vergütete Einheiten als Vorschuss/Plusstunden zählen</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-500">IBAN (für SEPA-Überweisung)</label>
              <Input value={form.iban || ''} onChange={(e) => set('iban', e.target.value)} className="mt-1 font-mono" data-testid="payroll-form-iban" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">BIC</label>
              <Input value={form.bic || ''} onChange={(e) => set('bic', e.target.value)} className="mt-1 font-mono" data-testid="payroll-form-bic" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Kontoinhaber (nur falls abweichend)</label>
            <Input
              value={form.account_holder || ''}
              onChange={(e) => set('account_holder', e.target.value)}
              className="mt-1"
              placeholder={nameFuerKonto || 'falls leer: Name der Person'}
              data-testid="payroll-form-account-holder"
            />
            <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!form.payout_blocked}
                onChange={(e) => set('payout_blocked', e.target.checked)}
                data-testid="payroll-form-payout-blocked"
              />
              <span>Auszahlung gesperrt. Das Honorar wird weiter berechnet und gebucht, aber nicht überwiesen. Der offene Betrag bleibt stehen.</span>
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Die Bank gleicht den Empfängernamen mit dem zur IBAN registrierten ab. Läuft das
              Konto auf jemand anderen — Ehepartner, Mädchenname, GbR — gehört dieser Name hier
              hinein. Bleibt das Feld leer, wird der Name der Person übertragen.
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Notizen</label>
            <Textarea value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} className="mt-1" />
          </div>
          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} data-testid="payroll-form-active" />
              Aktiv (wird in Läufen berücksichtigt)
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Abbrechen</Button>
              <Button onClick={save} disabled={saving} data-testid="payroll-form-save">
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Speichern
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WageReportSummary({ report }) {
  if (!report) return null;
  const num = (v) => formatDeNumber(v);
  // Reihenfolge wie in der Tabelle, mit der die Meldung von Hand geführt wurde:
  // Wochenstunden → Ferienüberhang → abzurechnende Stunden → Meldestunden.
  const rows = [
    ['Regelunterricht Lohn 1', `${num(report.week_hours_lohn1)} Std./Woche`],
    ['Regelunterricht Lohn 2', `${num(report.week_hours_lohn2)} Std./Woche`],
    [`Ferienüberhang (${report.vacation_share_label || '6/40'}) Lohn 1`, `${num(report.vacation_hours_lohn1)} Std./Woche`],
    [`Ferienüberhang (${report.vacation_share_label || '6/40'}) Lohn 2`, `${num(report.vacation_hours_lohn2)} Std./Woche`],
    ['Abzurechnende Stunden Lohn 1', `${num(report.billable_week_hours_lohn1)} Std./Woche`],
    ['Abzurechnende Stunden Lohn 2', `${num(report.billable_week_hours_lohn2)} Std./Woche`],
    ['Ferienfaktor', report.holiday_factor_label || num(report.holiday_factor)],
    ['Gemeldete Plusstunden', `${num(report.plus_hours_reported)} Std.`],
    ['davon genehmigte Plusstunden', `${num(report.plus_hours_approved)} Std.`],
    ['Monatsstunden Lohn 1 (Regel)', `${num(report.month_hours_lohn1)} Std.`],
    ['Monatsstunden Lohn 1 inkl. Plusstunden', `${num(report.month_hours_lohn1_incl_plus ?? report.month_hours_lohn1)} Std.`],
    ['Monatsstunden Lohn 2', `${num(report.month_hours_lohn2)} Std.`],
  ];
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/80 overflow-hidden max-w-lg" data-testid="payroll-wage-report">
      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 bg-slate-100 border-b border-slate-200">
        Lohnmeldung (automatisch)
      </div>
      <table className="w-full text-xs">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className="border-t border-slate-100">
              <td className="py-1.5 px-3 text-slate-600">{label}</td>
              <td className="py-1.5 px-3 text-right font-semibold text-slate-800 tabular-nums">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const RHYTHM_LABEL = { weekly: 'wöchentlich', biweekly: '14-tägig', monthly: 'monatlich', individual: 'individuell' };
const BAND_LABEL = { lohn1: 'Lohn 1', lohn2: 'Lohn 2' };

function groupRhythmLabel(g) {
  const rhythms = [...new Set((g.details || []).map((d) => d.rhythm).filter(Boolean))];
  if (rhythms.length === 1) return RHYTHM_LABEL[rhythms[0]] || rhythms[0];
  if (rhythms.length > 1) return 'gemischt';
  return '';
}

function GroupedStatementTable({ groups, wageReport, stickyTitle }) {
  const [open, setOpen] = useState({});
  if (!groups || groups.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-slate-400 italic py-2">Keine Positionen.</p>
        <WageReportSummary report={wageReport} />
      </div>
    );
  }
  const num = (v) => formatDeNumber(v);
  const colCount = 11; // chevron + 10 data cols
  return (
    <div className="space-y-2" data-testid="payroll-grouped-wrap">
      <p className="text-[11px] text-slate-500 px-1">
        Honorar = Unterricht (Soll/Ist bzw. Umsatzbeteiligung) · Kopf-Pauschale = Belegung ·
        Zusatz = Plusstunden / Fahrtkosten / manuelle Positionen.
        Anteilige Eintritte/Austritte sind bei den Schülernamen ausgewiesen.
      </p>
      <div
        className="rounded-lg border border-slate-200 bg-white max-h-[min(70vh,640px)] overflow-auto"
        data-testid="payroll-grouped-table"
      >
        <table className="w-full text-xs border-separate border-spacing-0">
          <thead>
            {stickyTitle && (
              <tr>
                <th
                  colSpan={colCount}
                  className="sticky top-0 z-30 bg-slate-200 text-left py-2 px-3 text-sm font-semibold text-slate-800 normal-case tracking-normal border-b border-slate-300"
                >
                  {stickyTitle}
                </th>
              </tr>
            )}
            <tr className="bg-slate-100 text-slate-600 uppercase tracking-wide">
              <th className={`text-left py-1.5 px-2 w-6 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}></th>
              <th className={`text-left py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Anz.</th>
              <th className={`text-left py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Veranstaltung</th>
              <th className={`text-right py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>TN</th>
              <th className={`text-right py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>UE / Woche</th>
              <th className={`text-right py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Termine / Monat</th>
              <th className={`text-left py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Rhythmus</th>
              <th className={`text-right py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Honorar</th>
              <th className={`text-right py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Kopf-Pauschale</th>
              <th className={`text-right py-1.5 px-2 sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Zusatz</th>
              <th className={`text-right py-1.5 px-2 font-semibold sticky z-20 bg-slate-100 border-b border-slate-200 ${stickyTitle ? 'top-10' : 'top-0'}`}>Gesamt</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => {
              const isOpen = !!open[gi];
              const hasDetails = (g.details || []).length > 0;
              return (
                <React.Fragment key={gi}>
                  <tr
                    className={`border-t border-slate-100 ${hasDetails ? 'cursor-pointer hover:bg-brand-red/5' : ''}`}
                    onClick={() => hasDetails && setOpen((o) => ({ ...o, [gi]: !o[gi] }))}
                    data-testid={`payroll-group-row-${gi}`}
                  >
                    <td className="py-1.5 px-2">
                      {hasDetails && (isOpen ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />)}
                    </td>
                    <td className="py-1.5 px-2 font-semibold text-slate-700">{g.count_courses > 0 ? `${g.count_courses} ×` : ''}</td>
                    <td className="py-1.5 px-2 text-slate-800">
                      {g.veranstaltung}
                      {(g.component_labels || []).includes('Individuelles Veranstaltungshonorar') && (
                        <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">
                          Individuelles Veranstaltungshonorar
                        </Badge>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right">{g.n_teilnehmer || ''}</td>
                    <td className="py-1.5 px-2 text-right">{g.ue_pro_woche ? num(g.ue_pro_woche) : ''}</td>
                    <td className="py-1.5 px-2 text-right">{g.termine_monat || ''}</td>
                    <td className="py-1.5 px-2 text-slate-600">{groupRhythmLabel(g)}</td>
                    <td className="py-1.5 px-2 text-right">{g.honorar ? `${num(g.honorar)} €` : ''}</td>
                    <td className="py-1.5 px-2 text-right">{g.kopf_pauschale ? `${num(g.kopf_pauschale)} €` : ''}</td>
                    <td className="py-1.5 px-2 text-right">{g.zusatz ? `${num(g.zusatz)} €` : ''}</td>
                    <td className="py-1.5 px-2 text-right font-semibold">{num(g.gesamt)} €</td>
                  </tr>
                  {isOpen && hasDetails && g.details.map((d, di) => {
                    const when = `${d.day_label || ''} ${formatTime(d.start_time) || ''}`.trim();
                    const students = (d.student_details || []).length > 0
                      ? d.student_details.map((sd) => sd.name).join(', ')
                      : (d.students || []).join(', ');
                    const label = [when, students].filter(Boolean).join(' · ') || d.course_name || 'Kurs';
                    return (
                      <tr
                        key={`${gi}-${di}`}
                        className="bg-brand-red/5 border-t border-brand-red/10"
                        data-testid={`payroll-group-detail-${gi}-${di}`}
                      >
                        <td className="py-1 px-2"></td>
                        <td className="py-1 px-2"></td>
                        <td className="py-1 px-2 text-slate-700 pl-4">
                          <span>{label}</span>
                          {d.individual_course_honorarium && (
                            <span className="ml-1.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                              Individuelles Veranstaltungshonorar
                            </span>
                          )}
                          {(d.student_details || []).filter((sd) => sd.prorated && sd.note).map((sd) => (
                            <span
                              key={sd.name}
                              className="ml-1.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                            >
                              {sd.name}: {sd.note}
                            </span>
                          ))}
                        </td>
                        <td className="py-1 px-2 text-right">{d.n_enrollments || ''}</td>
                        <td className="py-1 px-2 text-right tabular-nums text-slate-700">
                          {d.ue_pro_woche ? num(d.ue_pro_woche) : ''}
                          {d.wage_band ? (
                            <span className="block text-[10px] text-slate-500 font-normal">
                              {BAND_LABEL[d.wage_band] || d.wage_band}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-1 px-2 text-right">{d.termine_monat || ''}</td>
                        <td className="py-1 px-2 text-slate-500">
                          {RHYTHM_LABEL[d.rhythm] || d.rhythm || '—'}
                          {d.prepaid ? ' · Karte' : ''}
                        </td>
                        <td className="py-1 px-2 text-right tabular-nums">{d.honorar ? `${num(d.honorar)} €` : ''}</td>
                        <td className="py-1 px-2 text-right tabular-nums">{d.kopf_pauschale ? `${num(d.kopf_pauschale)} €` : ''}</td>
                        <td className="py-1 px-2 text-right tabular-nums">{d.zusatz ? `${num(d.zusatz)} €` : ''}</td>
                        <td className="py-1 px-2 text-right tabular-nums font-semibold">{d.gesamt ? `${num(d.gesamt)} €` : ''}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50 font-semibold text-slate-700">
            <tr className="border-t-2 border-slate-300">
              <td colSpan={2} className="py-2 px-2 text-right">Summen:</td>
              <td className="py-2 px-2">{groups.reduce((a, g) => a + (g.count_courses || 0), 0)} Veranstaltungen</td>
              <td className="py-2 px-2 text-right">{groups.reduce((a, g) => a + (g.n_teilnehmer || 0), 0)}</td>
              <td className="py-2 px-2 text-right">{num(groups.reduce((a, g) => a + (g.ue_pro_woche || 0), 0))}</td>
              <td className="py-2 px-2 text-right">{groups.reduce((a, g) => a + (g.termine_monat || 0), 0)}</td>
              <td className="py-2 px-2"></td>
              <td className="py-2 px-2 text-right">{num(groups.reduce((a, g) => a + (g.honorar || 0), 0))} €</td>
              <td className="py-2 px-2 text-right">{num(groups.reduce((a, g) => a + (g.kopf_pauschale || 0), 0))} €</td>
              <td className="py-2 px-2 text-right">{num(groups.reduce((a, g) => a + (g.zusatz || 0), 0))} €</td>
              <td className="py-2 px-2 text-right">{num(groups.reduce((a, g) => a + (g.gesamt || 0), 0))} €</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <WageReportSummary report={wageReport} />
    </div>
  );
}


const SOURCE_LABEL = { auto: 'Auto', manual: 'Manuell', pdf: 'PDF (KI)', carryover: 'Vormonat' };
const SOURCE_STYLE = {
  auto: 'bg-slate-100 text-slate-600',
  manual: 'bg-blue-100 text-blue-700',
  pdf: 'bg-brand-red/10 text-brand-red',
  carryover: 'bg-amber-100 text-amber-700',
};

function WageRow({ ws, onReload }) {
  const [gross, setGross] = useState('');
  const [deds, setDeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setGross((ws.gross_amount || 0).toFixed(2).replace('.', ','));
    setDeds((ws.deductions || []).map((d) => ({ label: d.label, amount: String(d.amount).replace('.', ',') })));
    setDirty(false);
  }, [ws]);

  const num = (v) => parseFloat(String(v).replace(',', '.')) || 0;
  const dedTotal = deds.reduce((a, d) => a + num(d.amount), 0);
  const net = num(gross) - dedTotal;

  const save = async () => {
    setBusy('save');
    try {
      await api.put(`/payroll/wage-statements/${ws.id}`, {
        gross_amount: num(gross),
        deductions: deds.filter((d) => (d.label || '').trim()).map((d) => ({ label: d.label.trim(), amount: num(d.amount) })),
      });
      toast.success('Lohnerfassung gespeichert');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  const resetGross = async () => {
    setBusy('reset');
    try {
      await api.put(`/payroll/wage-statements/${ws.id}`, { gross_reset: true });
      toast.success('Brutto auf automatische Berechnung zurückgesetzt');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Zurücksetzen fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  const carryover = async () => {
    setBusy('carry');
    try {
      await api.post(`/payroll/wage-statements/${ws.id}/carryover`);
      toast.success('Abzüge aus dem Vormonat übernommen');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Vormonat nicht verfügbar'); }
    finally { setBusy(null); }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setBusy('pdf');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/payroll/wage-statements/${ws.id}/parse-pdf`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const ex = r.data.extracted || {};
      const hint = [ex.employee_name, ex.period].filter(Boolean).join(', ');
      toast.success(`PDF ausgelesen${hint ? ` (${hint}) – bitte prüfen` : ' – bitte prüfen'}`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'PDF-Auslesung fehlgeschlagen'); }
    finally { setBusy(null); if (fileRef.current) fileRef.current.value = ''; }
  };

  const setDed = (idx, k, v) => { setDeds(deds.map((d, i) => (i === idx ? { ...d, [k]: v } : d))); setDirty(true); };

  return (
    <React.Fragment>
      <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`wage-row-${ws.profile_id}`}>
        <td className="py-2 px-2">
          <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-700" data-testid={`wage-expand-${ws.profile_id}`}>
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="py-2 px-2 font-medium text-slate-800">
          {ws.name}
          {ws.personnel_number && <span className="ml-2 text-[10px] font-mono text-slate-400">PN {ws.personnel_number}</span>}
        </td>
        <td className="py-2 px-2 text-right">
          <div className="flex items-center justify-end gap-1">
            {ws.gross_is_override && (
              <button onClick={resetGross} disabled={!!busy} className="p-1 text-brand-red hover:text-amber-700" title={`Manuell geändert – zurück zu Auto (${fmtEur(ws.gross_auto)})`} data-testid={`wage-gross-reset-${ws.profile_id}`}>
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
            <Input value={gross} onChange={(e) => { setGross(e.target.value); setDirty(true); }}
                   className="w-28 h-8 text-right text-sm" data-testid={`wage-gross-input-${ws.profile_id}`} />
          </div>
        </td>
        <td className="py-2 px-2 text-right text-rose-600">{dedTotal > 0 ? `− ${fmtEur(dedTotal)}` : '—'}</td>
        <td className="py-2 px-2 text-right font-semibold" data-testid={`wage-net-${ws.profile_id}`}>{fmtEur(net)}</td>
        <td className="py-2 px-2">
          <Badge className={`border-0 text-[10px] ${SOURCE_STYLE[ws.source] || SOURCE_STYLE.auto}`}>{SOURCE_LABEL[ws.source] || ws.source}</Badge>
        </td>
        <td className="py-2 px-2 text-right whitespace-nowrap">
          {dirty && (
            <button onClick={save} disabled={!!busy} className="p-1.5 text-emerald-600 hover:text-emerald-800" title="Speichern" data-testid={`wage-save-${ws.profile_id}`}>
              {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            </button>
          )}
          <button onClick={() => fileRef.current?.click()} disabled={!!busy}
                  className="p-1.5 text-slate-400 hover:text-brand-red" title="Steuerberater-PDF hochladen (KI liest Brutto/Abzüge/Netto aus)"
                  data-testid={`wage-pdf-upload-${ws.profile_id}`}>
            {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
          </button>
          <button onClick={carryover} disabled={!!busy || !ws.prev_available}
                  className="p-1.5 text-slate-400 hover:text-brand-red disabled:opacity-30 disabled:cursor-not-allowed"
                  title={ws.prev_available ? 'Abzüge aus Vormonat übernehmen' : 'Keine Abzüge im Vormonat vorhanden'}
                  data-testid={`wage-carryover-${ws.profile_id}`}>
            {busy === 'carry' ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
          </button>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
                 onChange={(e) => uploadPdf(e.target.files?.[0])} data-testid={`wage-pdf-input-${ws.profile_id}`} />
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/40">
          <td></td>
          <td colSpan={6} className="py-2 px-2">
            {(ws.grouped_items || []).length > 0 ? (
              <div className="mb-3" data-testid={`wage-grouped-${ws.profile_id}`}>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Unterrichtseinheiten &amp; Vergütungskomponenten <span className="normal-case font-normal text-slate-400">(Basis für Auto-Brutto {fmtEur(ws.gross_auto)})</span>
                </div>
                <GroupedStatementTable groups={ws.grouped_items} wageReport={ws.wage_report} />
              </div>
            ) : (
              <div className="mb-3 space-y-2">
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5" data-testid={`wage-grouped-empty-${ws.profile_id}`}>
                  Keine berechneten Unterrichtseinheiten in diesem Lauf – bitte oben „Neu berechnen" klicken (z. B. nach Änderung der Sätze im Profil) oder Vergütungssätze im Profil prüfen.
                </p>
                <WageReportSummary report={ws.wage_report} />
              </div>
            )}
            <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 max-w-xl" data-testid={`wage-deductions-${ws.profile_id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Abzüge (lt. Steuerberater)</span>
                <Button variant="outline" size="sm" className="h-7 text-xs"
                        onClick={() => { setDeds([...deds, { label: '', amount: '' }]); setDirty(true); }}
                        data-testid={`wage-add-deduction-${ws.profile_id}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Abzug
                </Button>
              </div>
              {deds.length === 0 && <p className="text-xs text-slate-400">Keine Abzüge erfasst – per PDF-Upload, Vormonat oder manuell hinzufügen.</p>}
              {deds.map((d, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input value={d.label} onChange={(e) => setDed(idx, 'label', e.target.value)}
                         placeholder="z. B. Lohnsteuer" className="flex-1 h-8 text-xs" data-testid={`wage-ded-label-${ws.profile_id}-${idx}`} />
                  <Input value={d.amount} onChange={(e) => setDed(idx, 'amount', e.target.value)}
                         placeholder="0,00" className="w-28 h-8 text-xs text-right" data-testid={`wage-ded-amount-${ws.profile_id}-${idx}`} />
                  <button onClick={() => { setDeds(deds.filter((_, i) => i !== idx)); setDirty(true); }}
                          className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-xs">
                <span className="text-slate-500">Brutto {fmtEur(num(gross))} − Abzüge {fmtEur(dedTotal)}</span>
                <span className="font-semibold text-slate-800">Netto: {fmtEur(net)}</span>
              </div>
              {dirty && (
                <Button size="sm" className="h-7 text-xs" onClick={save} disabled={!!busy} data-testid={`wage-save-inline-${ws.profile_id}`}>
                  {busy === 'save' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Speichern
                </Button>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function WageSection({ run, download }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/payroll/runs/${run.id}/wage-statements`);
      setRows(r.data);
    } catch { setRows([]); }
  }, [run.id]);

  useEffect(() => { load(); }, [load]);

  const carryoverAll = async () => {
    if (!window.confirm('Abzüge des Vormonats für ALLE Angestellten übernehmen? Bereits erfasste Abzüge (z. B. aus PDF) werden überschrieben.')) return;
    setBusy('carry-all');
    try {
      const r = await api.post(`/payroll/runs/${run.id}/wage-statements/carryover-all`);
      toast.success(`${r.data.updated} übernommen · ${r.data.skipped} ohne Vormonatsdaten`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Übernahme fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  if (!rows || rows.length === 0) return null;
  const missingPn = rows.filter((r) => !r.personnel_number).map((r) => r.name);

  return (
    <div className="mt-6 space-y-2" data-testid="wage-section">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold text-slate-800">Lohnerfassung Angestellte <span className="font-normal text-slate-400 text-xs">(Werte lt. Steuerberater – Abrechnung nach § 108 GewO)</span></h3>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={carryoverAll} disabled={!!busy} data-testid="wage-carryover-all">
          {busy === 'carry-all' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <History className="h-4 w-4 mr-1" />} Vormonat für alle
        </Button>
        <Button variant="outline" size="sm" onClick={() => download(`/payroll/runs/${run.id}/export-lohnbuero.csv`, `lohnbuero_wolterskluwer_${run.period}.csv`)} data-testid="wage-export-lohnbuero">
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Lohnbüro (Wolters Kluwer)
        </Button>
      </div>
      {missingPn.length > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5" data-testid="wage-missing-pn-warning">
          Ohne Personalnummer (für den Lohnbüro-Export im Profil ergänzen): {missingPn.join(', ')}
        </p>
      )}
      <table className="w-full text-sm" data-testid="wage-table">
        <thead>
          <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
            <th className="text-left py-2 px-2 w-6"></th>
            <th className="text-left py-2 px-2">Name</th>
            <th className="text-right py-2 px-2">Brutto</th>
            <th className="text-right py-2 px-2">Abzüge</th>
            <th className="text-right py-2 px-2">Netto</th>
            <th className="text-left py-2 px-2">Quelle</th>
            <th className="text-right py-2 px-2">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((ws) => <WageRow key={ws.id} ws={ws} onReload={load} />)}
        </tbody>
      </table>
    </div>
  );
}

function ManualItemsEditor({ run, statement, onChanged, templates, onTemplatesChanged }) {
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState('');
  const [tplId, setTplId] = useState(null);
  const [busy, setBusy] = useState(false);
  const num = (v) => parseFloat(String(v).replace(',', '.')) || 0;
  const manuals = (statement.items || []).filter((i) => i.component === 'manual');
  const isFinal = run.status === 'final';

  const applyTemplate = (t) => {
    setDesc(t.label);
    setAmount(String(t.amount).replace('.', ','));
    setTplId(t.id);
  };

  const add = async () => {
    if (!desc.trim()) { toast.error('Beschreibung angeben (z. B. Fahrtkosten)'); return; }
    if (!num(amount)) { toast.error('Betrag angeben (negativ = Abzug)'); return; }
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${run.id}/manual-items`, {
        profile_id: statement.profile_id, description: desc.trim(), amount: num(amount),
        template_id: tplId,
      });
      toast.success('Eingabe hinzugefügt');
      setDesc(''); setAmount(''); setTplId(null);
      onChanged();
      if (onTemplatesChanged) onTemplatesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hinzufügen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const saveAsTemplate = async () => {
    if (!desc.trim()) { toast.error('Beschreibung angeben, um eine Vorlage zu speichern'); return; }
    setBusy(true);
    try {
      await api.post('/payroll/entry-templates', { label: desc.trim(), amount: num(amount) });
      toast.success(`Vorlage „${desc.trim()}" gespeichert`);
      if (onTemplatesChanged) onTemplatesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Vorlage konnte nicht gespeichert werden'); }
    finally { setBusy(false); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Vorlage „${t.label}" löschen?`)) return;
    try {
      await api.delete(`/payroll/entry-templates/${t.id}`);
      if (onTemplatesChanged) onTemplatesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  const remove = async (itemId) => {
    setBusy(true);
    try {
      await api.delete(`/payroll/runs/${run.id}/manual-items/${itemId}`);
      toast.success('Eingabe entfernt');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Entfernen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  if (isFinal && manuals.length === 0) return null;

  return (
    <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-white p-3 space-y-2" data-testid={`manual-items-${statement.profile_id}`}>
      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
        Manuelle Eingabe <span className="normal-case font-normal text-slate-400">(z. B. Fahrtkosten als Zuschlag, Abzug mit Minus – bleiben bei Neuberechnung erhalten)</span>
      </div>
      {manuals.length === 0 && <p className="text-xs text-slate-400">Keine manuellen Eingaben erfasst.</p>}
      {manuals.map((m) => (
        <div key={m.id} className="flex items-center gap-2 text-xs" data-testid={`manual-item-row-${m.id}`}>
          <span className="flex-1 text-slate-700">{m.description}</span>
          <span className={`font-semibold ${m.amount < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
            {m.amount < 0 ? '−' : '+'} {fmtEur(Math.abs(m.amount))}
          </span>
          {!isFinal && (
            <button onClick={() => remove(m.id)} disabled={busy}
                    className="p-1 text-slate-400 hover:text-rose-600" title="Eingabe entfernen"
                    data-testid={`manual-item-delete-${m.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {!isFinal && (
        <>
          {(templates || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid={`manual-templates-${statement.profile_id}`}>
              {templates.map((t) => (
                <span key={t.id}
                      className={`group inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] cursor-pointer transition-colors ${tplId === t.id ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-brand-red/40 hover:bg-brand-red/5'}`}
                      onClick={() => applyTemplate(t)}
                      title={`Klick übernimmt „${t.label}" mit Merkbetrag ${fmtEur(t.amount)}`}
                      data-testid={`manual-template-chip-${t.id}`}>
                  {t.label}
                  <span className={`font-semibold ${t.amount < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                    {t.amount < 0 ? '−' : ''}{fmtEur(Math.abs(t.amount))}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition-opacity"
                          title="Vorlage löschen" data-testid={`manual-template-delete-${t.id}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Input value={desc} onChange={(e) => { setDesc(e.target.value); setTplId(null); }}
                   placeholder="z. B. Fahrtkosten Juli / Vorschuss-Abzug"
                   className="flex-1 h-8 text-xs" data-testid={`manual-item-desc-${statement.profile_id}`} />
            <Input value={amount} onChange={(e) => setAmount(e.target.value)}
                   placeholder="+25,00 / -10,00"
                   className="w-32 h-8 text-xs text-right" data-testid={`manual-item-amount-${statement.profile_id}`} />
            <Button size="sm" className="h-8 text-xs" onClick={add} disabled={busy}
                    data-testid={`manual-item-add-${statement.profile_id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />} Hinzufügen
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={saveAsTemplate} disabled={busy}
                    title="Beschreibung + Betrag als neue Vorlage speichern"
                    data-testid={`manual-item-save-template-${statement.profile_id}`}>
              <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Als Vorlage
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function RunDetail({ run, onChanged }) {
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);
  const [mailing, setMailing] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [pdfView, setPdfView] = useState(null); // { url, title }
  const [mailPreview, setMailPreview] = useState(null); // { url, profileId, name, previewToken, recipient }
  const [mailAllPreview, setMailAllPreview] = useState(null);
  const [fintsOpen, setFintsOpen] = useState(false);
  const [fintsZeilen, setFintsZeilen] = useState([]);
  const [gewaehlt, setGewaehlt] = useState({});

  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.get('/payroll/entry-templates');
      setTemplates(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const apiBase = api.defaults.baseURL;

  const download = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Download fehlgeschlagen';
      if (e.response?.data) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ }
      }
      toast.error(msg);
    }
  };

  const loadPdfUrl = async (path) => {
    const r = await api.get(path, { responseType: 'blob' });
    return URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
  };

  const openPdfPreview = async (profileId, name) => {
    try {
      const url = await loadPdfUrl(`/payroll/runs/${run.id}/statement/${profileId}/pdf`);
      setPdfView({ url, title: `Abrechnung ${run.period} — ${name}` });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF-Voransicht fehlgeschlagen');
    }
  };

  const openMailPreview = async (profileId, name) => {
    try {
      const url = await loadPdfUrl(`/payroll/runs/${run.id}/statement/${profileId}/pdf`);
      let previewToken = null;
      let recipient = null;
      try {
        const r = await api.get(`/payroll/runs/${run.id}/statement/${profileId}/email-preview`);
        previewToken = r.data?.preview_token || null;
        recipient = r.data?.recipient || null;
      } catch { /* Token-Pflicht serverseitig */ }
      setMailPreview({ url, profileId, name, previewToken, recipient });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Voransicht konnte nicht geladen werden');
    }
  };

  const closePdfView = () => {
    if (pdfView?.url) URL.revokeObjectURL(pdfView.url);
    setPdfView(null);
  };

  const closeMailPreview = () => {
    if (mailPreview?.url) URL.revokeObjectURL(mailPreview.url);
    setMailPreview(null);
  };

  const mailOne = async (routing = {}) => {
    if (!mailPreview) return;
    if (!mailPreview.previewToken) {
      toast.error('Voransicht unvollständig — bitte erneut öffnen');
      return;
    }
    const { profileId, name } = mailPreview;
    setMailing(profileId);
    try {
      const r = await api.post(`/payroll/runs/${run.id}/statement/${profileId}/email`, {
        preview_token: mailPreview.previewToken,
        ...routing,
      });
      toast.success(r.data.queued ? `Abrechnung an ${r.data.recipient} in Freigabe-Queue` : `Abrechnung an ${r.data.recipient} gesendet`);
      closeMailPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Versand an ${name} fehlgeschlagen`);
    } finally { setMailing(null); }
  };

  const openMailAll = async () => {
    setMailAllPreview({ loading: true, recipients: [], skipped: [] });
    try {
      const r = await api.get(`/payroll/runs/${run.id}/email-all-preview`);
      setMailAllPreview({
        loading: false,
        period: r.data.period,
        recipients: (r.data.recipients || []).map((x) => ({
          id: x.profile_id,
          label: x.name,
          email: x.email,
        })),
        skipped: (r.data.skipped || []).map((x) => ({ label: x.name, reason: x.reason })),
      });
    } catch (e) {
      setMailAllPreview(null);
      toast.error(e.response?.data?.detail || 'Empfängerliste konnte nicht geladen werden');
    }
  };

  const confirmMailAll = async (payload = {}) => {
    const { profile_ids, ...routing } = payload;
    setMailing('all');
    try {
      const r = await api.post(`/payroll/runs/${run.id}/email-all`, {
        preview_confirmed: true,
        profile_ids: profile_ids || [],
        ...routing,
      });
      const parts = [`${r.data.sent} gesendet`];
      if (r.data.skipped_no_email?.length) parts.push(`${r.data.skipped_no_email.length} ohne E-Mail übersprungen`);
      if (r.data.failed?.length) parts.push(`${r.data.failed.length} fehlgeschlagen`);
      toast.success(parts.join(' · '));
      if (r.data.failed?.length) {
        toast.error('Fehler bei: ' + r.data.failed.join('; '));
      }
      setMailAllPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sammelversand fehlgeschlagen');
    } finally { setMailing(null); }
  };

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="payroll-run-detail">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={run.status === 'final' ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-amber-100 text-amber-700 border-0'}>
          {run.status === 'final' ? 'Final' : 'Entwurf'}
        </Badge>
        <span className="text-sm font-semibold text-slate-800">Summe: {fmtEur(run.total_amount)}</span>
        <Badge variant="secondary" className="text-[10px]" data-testid="payroll-run-basis-badge">
          {run.hourly_basis === 'attendance' ? 'Ist-Stunden (Anwesenheit)' : 'Soll-Stunden (Stundenplan)'}
        </Badge>
        <div className="flex-1" />
        {run.status !== 'final' && (
          <>
            <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/payroll/runs/${run.id}/recalculate`), 'recalc')} disabled={!!busy} data-testid="payroll-run-recalc">
              {busy === 'recalc' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Neu berechnen
            </Button>
            <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/payroll/runs/${run.id}/finalize`), 'final')} disabled={!!busy} data-testid="payroll-run-finalize">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Finalisieren
            </Button>
          </>
        )}
        <Button variant="outline" size="sm" onClick={() => download(`/payroll/runs/${run.id}/export-lohnmeldung.csv`, `lohnmeldung_${run.period}.csv`)} data-testid="payroll-run-lohnmeldung" title="Nur Angestellte. Die Beschäftigungsart kommt aus dem Mitarbeiterdatensatz.">
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Lohnmeldung exportieren
        </Button>
        <Button variant="outline" size="sm" onClick={() => download(`/payroll/runs/${run.id}/export.csv`, `honorare_${run.period}.csv`)} data-testid="payroll-run-csv">
          <FileDown className="h-4 w-4 mr-1" /> CSV (Lohnbüro)
        </Button>
        <Button variant="outline" size="sm" onClick={() => download(`/payroll/runs/${run.id}/sepa.xml`, `ueberweisungen_${run.period}.xml`)} data-testid="payroll-run-sepa">
          <Landmark className="h-4 w-4 mr-1" /> SEPA-Überweisungen
        </Button>
        <Button variant="outline" size="sm" onClick={() => { setFintsZeilen([]); setFintsOpen(true); }} data-testid="payroll-run-fints" title="Direkt an die Bank senden">
          <Landmark className="h-4 w-4 mr-1" /> An die Bank
        </Button>
        {Object.keys(gewaehlt).some((id) => gewaehlt[id]) && (
          <Button variant="outline" size="sm" data-testid="payroll-fints-auswahl" onClick={() => {
            setFintsZeilen(Object.keys(gewaehlt).filter((id) => gewaehlt[id]));
            setFintsOpen(true);
          }}>
            <Landmark className="h-4 w-4 mr-1" /> Ausgewählte an die Bank
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={openMailAll} disabled={mailing === 'all'} data-testid="payroll-run-mail-all">
          {mailing === 'all' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Alle mailen
        </Button>
      </div>
      <FintsSubmitDialog
        open={fintsOpen}
        onOpenChange={setFintsOpen}
        targetType="payroll"
        targetId={run.id}
        lineIds={fintsZeilen.length ? fintsZeilen : undefined}
        onSubmitted={onChanged}
      />
      {(run.missing_iban || []).length > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
          Ohne IBAN (fehlen in der SEPA-Datei): {run.missing_iban.join(', ')}
        </p>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
            <th className="text-left py-2 px-2 w-6"></th>
            <th className="text-left py-2 px-2 w-6"></th>
            <th className="text-left py-2 px-2">Name</th>
            <th className="text-left py-2 px-2">Typ</th>
            <th className="text-left py-2 px-2">IBAN</th>
            <th className="text-right py-2 px-2">Gesamt</th>
            <th className="text-right py-2 px-2">PDF</th>
            <th className="text-right py-2 px-2">E-Mail</th>
          </tr>
        </thead>
        <tbody>
          {run.statements.map((s) => (
            <React.Fragment key={s.profile_id}>
              <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payroll-statement-${s.profile_id}`}>
                <td className="py-2 px-2">
                  {s.total > 0 && s.iban ? (
                    <input
                      type="checkbox"
                      checked={!!gewaehlt[s.profile_id]}
                      onChange={() => setGewaehlt((alt) => ({ ...alt, [s.profile_id]: !alt[s.profile_id] }))}
                      aria-label={`Honorarüberweisung ${s.name || ''} auswählen`}
                      data-testid={`payroll-fints-line-${s.profile_id}`}
                    />
                  ) : null}
                </td>
                <td className="py-2 px-2">
                  <button onClick={() => setExpanded((e) => ({ ...e, [s.profile_id]: !e[s.profile_id] }))} className="text-slate-400 hover:text-slate-700">
                    {expanded[s.profile_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </td>
                <td className="py-2 px-2 font-medium text-slate-800">
                  {s.name}
                  {s.payout_note && (
                    <div className="text-xs font-normal text-amber-800" data-testid={`payroll-payout-note-${s.profile_id}`}>
                      {s.payout_note}
                    </div>
                  )}
                </td>
                <td className="py-2 px-2 text-xs text-slate-500">{s.employment_type === 'angestellt' ? 'Festangestellt' : 'Honorarkraft'}</td>
                <td className="py-2 px-2 text-xs font-mono text-slate-500">{s.iban || <span className="text-brand-red">fehlt</span>}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmtEur(s.total)}</td>
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  <button onClick={() => openPdfPreview(s.profile_id, s.name)}
                          className="p-1.5 text-slate-400 hover:text-brand-red" title="PDF-Voransicht" data-testid={`payroll-preview-${s.profile_id}`}>
                    <Eye className="h-4 w-4" />
                  </button>
                  <button onClick={() => download(`/payroll/runs/${run.id}/statement/${s.profile_id}/pdf`, `abrechnung_${run.period}_${s.name}.pdf`)}
                          className="p-1.5 text-slate-400 hover:text-brand-red" title="PDF herunterladen" data-testid={`payroll-pdf-${s.profile_id}`}>
                    <FileText className="h-4 w-4" />
                  </button>
                </td>
                <td className="py-2 px-2 text-right">
                  <button onClick={() => openMailPreview(s.profile_id, s.name)} disabled={mailing === s.profile_id || s.total <= 0}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
                          title={s.total <= 0 ? 'Kein Betrag – kein Versand' : 'Per E-Mail senden (mit Voransicht)'}
                          data-testid={`payroll-mail-${s.profile_id}`}>
                    {mailing === s.profile_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  </button>
                </td>
              </tr>
              {expanded[s.profile_id] && (
                <tr className="bg-slate-50/40">
                  <td></td>
                  <td></td>
                  <td colSpan={6} className="py-2 px-2">
                    <GroupedStatementTable
                      groups={s.grouped_items || []}
                      wageReport={s.wage_report}
                      stickyTitle={`${s.name} · ${s.employment_type === 'angestellt' ? 'Festangestellt' : 'Honorarkraft'} · ${fmtEur(s.total)}`}
                    />
                    <ManualItemsEditor run={run} statement={s} onChanged={onChanged}
                                       templates={templates} onTemplatesChanged={loadTemplates} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {run.statements.length === 0 && (
            <tr><td colSpan={8} className="py-8 text-center text-slate-400">Keine Positionen – Profile prüfen (aktiv? Sätze &gt; 0?)</td></tr>
          )}
        </tbody>
      </table>
      <WageSection run={run} download={download} />

      <Dialog open={!!pdfView} onOpenChange={(o) => { if (!o) closePdfView(); }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="payroll-pdf-view-dialog">
          <DialogHeader>
            <DialogTitle>{pdfView?.title || 'Abrechnung'}</DialogTitle>
            <DialogDescription>Nur Voransicht — es wird nichts versendet.</DialogDescription>
          </DialogHeader>
          {pdfView?.url && (
            <iframe src={pdfView.url} title={pdfView.title} className="flex-1 w-full rounded border border-slate-200" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePdfView}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentEmailDialog
        open={!!mailPreview}
        onClose={closeMailPreview}
        onConfirm={mailOne}
        confirming={!!mailing && mailing !== 'all'}
        title={`Abrechnung per E-Mail — ${mailPreview?.name || ''}`}
        recipient={mailPreview?.recipient}
        recipientLabel={mailPreview?.name}
        pdfUrl={mailPreview?.url}
        testId="payroll-mail-preview-dialog"
        confirmTestId="payroll-mail-confirm"
        iframeTitle={`Voransicht ${mailPreview?.name || ''}`}
      />

      <BulkEmailRecipientsDialog
        open={!!mailAllPreview}
        onClose={() => { if (mailing !== 'all') setMailAllPreview(null); }}
        onConfirm={confirmMailAll}
        confirming={mailing === 'all'}
        loading={!!mailAllPreview?.loading}
        title={`Honorarabrechnungen ${mailAllPreview?.period || run.period} per E-Mail`}
        description="Honorarkräfte sind vorausgewählt. Festangestellte bleiben draußen, einzelne Personen können abgewählt werden."
        recipients={mailAllPreview?.recipients || []}
        skipped={mailAllPreview?.skipped || []}
        selectable
        testId="payroll-mail-all-dialog"
      />
    </div>
  );
}

export default function PayrollPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showBankImport, setShowBankImport] = useState(false);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [hourlyBasis, setHourlyBasis] = useState('schedule');
  const [creating, setCreating] = useState(false);
  const [openPlusHours, setOpenPlusHours] = useState(0);

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return PAYROLL_TABS.has(tab) ? tab : 'profiles';
  }, [searchParams]);

  useActionBarConfig({
    onNew: () => { setEditing(null); setShowDialog(true); },
    newLabel: 'Mitarbeiter / Profil anlegen',
  }, []);

  const loadPlusHoursSummary = useCallback(() => {
    api.get('/admin/plus-hours/summary')
      .then((r) => setOpenPlusHours(r.data?.open_plus_hours || 0))
      .catch(() => setOpenPlusHours(0));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fetchTeachers = async () => {
        const all = [];
        for (let offset = 0; offset < 2000; offset += 100) {
          const r = await api.get('/teachers', { params: { limit: 100, offset } });
          const batch = Array.isArray(r.data) ? r.data : r.data.items || [];
          all.push(...batch);
          if (batch.length < 100) break;
        }
        return all;
      };
      const [p, t, s, r] = await Promise.all([
        api.get('/payroll/profiles'),
        fetchTeachers(),
        api.get('/subjects'),
        api.get('/payroll/runs'),
      ]);
      setProfiles(p.data);
      setTeachers(t);
      setSubjects(Array.isArray(s.data) ? s.data : s.data.items || []);
      setRuns(r.data);
      loadPlusHoursSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen');
    } finally { setLoading(false); }
  }, [loadPlusHoursSummary]);

  useEffect(() => { load(); }, [load]);

  const syncTeachers = async () => {
    const r = await api.post('/payroll/profiles/sync-teachers');
    toast.success(`${r.data.created} Profile aus Lehrkräften angelegt`);
    load();
  };

  const openRun = async (id) => {
    try {
      const r = await api.get(`/payroll/runs/${id}`);
      setActiveRun(r.data);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error('Lauf nicht mehr vorhanden – Liste wird aktualisiert');
        setActiveRun(null);
        load();
      } else {
        toast.error(e.response?.data?.detail || 'Lauf konnte nicht geladen werden');
      }
    }
  };

  const createRun = async () => {
    setCreating(true);
    try {
      const r = await api.post('/payroll/runs', { period, hourly_basis: hourlyBasis });
      toast.success(`Lauf ${period} berechnet – Summe ${fmtEur(r.data.total_amount)}`);
      setActiveRun(r.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lauf konnte nicht erstellt werden');
    } finally { setCreating(false); }
  };

  const deleteRun = async (r) => {
    if (!window.confirm(`Lauf ${r.period} wirklich löschen?`)) return;
    try {
      await api.delete(`/payroll/runs/${r.id}`);
      toast.success(`Lauf ${r.period} gelöscht`);
      setRuns((prev) => prev.filter((x) => x.id !== r.id));
      if (activeRun?.id === r.id) setActiveRun(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const compBadges = (p) => {
    const b = [];
    if (p.monthly_salary > 0) b.push(`Fix ${fmtEur(p.monthly_salary)}`);
    if (p.hourly_rate > 0) b.push(`${fmtEur(p.hourly_rate)}/Std.`);
    if (p.per_enrollment_rate > 0) b.push(`${fmtEur(p.per_enrollment_rate)}/Belegung`);
    if (p.revenue_share_percent > 0) b.push(`${p.revenue_share_percent}% Umsatz`);
    return b;
  };

  return (
    <Layout>
      <div className="p-6 space-y-5" data-testid="payroll-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HandCoins className="h-6 w-6 text-brand-red" /> Honorare & Gehälter
          </h1>
          <p className="text-sm text-slate-500 mt-1">Vergütungsprofile, monatliche Abrechnungsläufe, Plusstunden, PDF-Abrechnungen, CSV- und SEPA-Export</p>
        </div>

        <SteuerberaterUpload />

        <Tabs
          value={activeTab}
          onValueChange={(tab) => {
            const next = new URLSearchParams(searchParams);
            if (tab === 'profiles') next.delete('tab');
            else next.set('tab', tab);
            setSearchParams(next, { replace: true });
          }}
          className="w-full min-w-0"
        >
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="profiles" data-testid="payroll-tab-profiles" className="shrink-0">
              <Users className="h-3.5 w-3.5 mr-1 shrink-0" /> Mitarbeiter & Sätze ({profiles.length})
            </TabsTrigger>
            <TabsTrigger value="runs" data-testid="payroll-tab-runs" className="shrink-0">
              <Calculator className="h-3.5 w-3.5 mr-1 shrink-0" /> Abrechnungsläufe ({runs.length})
            </TabsTrigger>
            <TabsTrigger value="plus-hours" data-testid="payroll-tab-plus-hours" className="shrink-0">
              <Clock className="h-3.5 w-3.5 mr-1 shrink-0" /> Plusstunden
              {openPlusHours > 0 && (
                <Badge className="ml-1.5 border-0 bg-brand-red text-white text-[10px] px-1.5 py-0">{openPlusHours}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profiles" className="mt-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Button variant="outline" size="sm" onClick={syncTeachers} data-testid="payroll-sync-teachers">
                    <RefreshCw className="h-4 w-4 mr-1.5" /> Profile aus Lehrkräften anlegen
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowBankImport(true)} data-testid="payroll-open-bank-import">
                    <Landmark className="h-4 w-4 mr-1.5" /> Bankdaten aus Überweisungsdatei
                  </Button>
                  <div className="flex-1" />
                  <Button size="sm" onClick={() => { setEditing(null); setShowDialog(true); }} data-testid="payroll-new-profile">
                    <Plus className="h-4 w-4 mr-1.5" /> Mitarbeiter / Profil
                  </Button>
                </div>
                {loading ? (
                  <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>
                ) : profiles.length === 0 ? (
                  <p className="py-12 text-center text-sm text-slate-400">Noch keine Profile – „Profile aus Lehrkräften anlegen" startet mit den Stundensätzen aus den Lehrer-Stammdaten.</p>
                ) : (
                  <table className="w-full text-sm" data-testid="payroll-profiles-table">
                    <thead>
                      <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                        <th className="text-left py-2 px-2">Name</th>
                        <th className="text-left py-2 px-2">Typ</th>
                        <th className="text-left py-2 px-2">Vergütung</th>
                        <th className="text-left py-2 px-2">IBAN</th>
                        <th className="text-right py-2 px-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {profiles.map((p) => (
                        <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!p.is_active ? 'opacity-50' : ''}`} data-testid={`payroll-profile-row-${p.id}`}>
                          <td className="py-2 px-2 font-medium text-slate-800">
                            {p.name}
                            {!p.teacher_id && <Badge variant="secondary" className="ml-2 text-[10px]">Verwaltung</Badge>}
                            {!p.is_active && <Badge variant="secondary" className="ml-2 text-[10px]">Inaktiv</Badge>}
                            {p.payout_blocked && <Badge className="ml-2 bg-amber-100 text-amber-800 border-0 text-[10px]">Auszahlung gesperrt</Badge>}
                          </td>
                          <td className="py-2 px-2 text-xs text-slate-500">{p.employment_type === 'angestellt' ? 'Festangestellt' : 'Honorarkraft'}</td>
                          <td className="py-2 px-2">
                            <div className="flex flex-wrap gap-1">
                              {compBadges(p).map((b, i) => <Badge key={i} variant="secondary" className="text-[10px]">{b}</Badge>)}
                              {p.overrides.length > 0 && <Badge className="bg-brand-red/10 text-brand-red border-0 text-[10px]">{p.overrides.length} Fach-Satz{p.overrides.length > 1 ? 'e' : ''}</Badge>}
                              {compBadges(p).length === 0 && <span className="text-xs text-brand-red">keine Sätze</span>}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-xs font-mono text-slate-500">{p.iban || <span className="text-brand-red">fehlt</span>}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            <button onClick={() => { setEditing(p); setShowDialog(true); }} className="p-1.5 text-slate-400 hover:text-brand-red" title="Bearbeiten" data-testid={`payroll-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                            <button onClick={async () => {
                              if (!window.confirm(`Profil „${p.name}" löschen?`)) return;
                              try { await api.delete(`/payroll/profiles/${p.id}`); load(); }
                              catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
                            }} className="p-1.5 text-slate-400 hover:text-rose-600" title="Löschen"><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="runs" className="mt-4 space-y-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <label className="text-sm text-slate-600">Monat:</label>
                  <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-44" data-testid="payroll-period-input" />
                  <label className="text-sm text-slate-600 ml-2">Stunden-Basis:</label>
                  <Select value={hourlyBasis} onValueChange={setHourlyBasis}>
                    <SelectTrigger className="w-64" data-testid="payroll-basis-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="schedule">Soll (Stundenplan-Termine)</SelectItem>
                      <SelectItem value="attendance">Ist (Anwesenheitslisten, erteilte Termine)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={createRun} disabled={creating} data-testid="payroll-create-run">
                    {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calculator className="h-4 w-4 mr-1.5" />} Lauf berechnen
                  </Button>
                </div>
                {runs.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4" data-testid="payroll-runs-list">
                    {runs.map((r) => (
                      <div key={r.id} className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm cursor-pointer transition-colors ${activeRun?.id === r.id ? 'border-brand-red/40 bg-brand-red/5' : 'border-slate-200 hover:border-slate-300'}`}
                           onClick={() => openRun(r.id)} data-testid={`payroll-run-chip-${r.period}`}>
                        <span className="font-medium">{r.period}</span>
                        <span className="text-[10px] text-slate-400">{r.hourly_basis === 'attendance' ? 'Ist' : 'Soll'}</span>
                        <span className="text-xs text-slate-500">{fmtEur(r.total_amount)}</span>
                        <Badge className={`border-0 text-[10px] ${r.status === 'final' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status === 'final' ? 'Final' : 'Entwurf'}</Badge>
                        {r.status !== 'final' && (
                          <button onClick={(e) => { e.stopPropagation(); deleteRun(r); }} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {activeRun ? (
                  <RunDetail run={activeRun} onChanged={() => { openRun(activeRun.id); load(); }} />
                ) : (
                  <p className="py-8 text-center text-sm text-slate-400">Lauf berechnen oder oben einen bestehenden Lauf auswählen.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="plus-hours" className="mt-4">
            <PlusHoursAdminTab onChange={loadPlusHoursSummary} />
          </TabsContent>
        </Tabs>

        <ProfileDialog open={showDialog} onClose={() => setShowDialog(false)} profile={editing} teachers={teachers} subjects={subjects}
                       onSaved={() => { load(); if (activeRun) openRun(activeRun.id); }} />
        <BankImportDialog open={showBankImport} onClose={() => setShowBankImport(false)} onDone={load} />
      </div>
    </Layout>
  );
}
